/*
 * Password Policy（Cognito と、患者の SAS 自身の認証の共通の規則）
 *
 * 最小文字数は 8 で、英大文字・英小文字・数字・記号をそれぞれ 1 文字以上含む（Cognito の User Pool と
 * backend2 の RawPassword の規則が同じ）。
 *
 * 使用できる記号は印字可能 ASCII 記号（AWS の仕様と、backend2 の RawPassword.isSymbol の範囲）として列挙します。
 * 患者の SAS 自身の認証では、生年月日を含む値・仮パスワード・今のパスワードと同じ値もサーバーが拒否する
 * （画面では検証せず、案内だけを出す。getPatientPolicyHelpText）。
 */

export type PasswordPolicy = {
  minLength: number;
  requireUppercase: boolean;
  requireLowercase: boolean;
  requireDigits: boolean;
  requireSymbols: boolean;
};

// 印字可能 ASCII 記号の列挙（スペース除く）
// 参考: !"#$%&'()*+,-./:;<=>?@[\]^_`{|}~
export const ALLOWED_SYMBOLS: string[] = [
  "!",
  '"',
  "#",
  "$",
  "%",
  "&",
  "'",
  "(",
  ")",
  "*",
  "+",
  ",",
  "-",
  ".",
  "/",
  ":",
  ";",
  "<",
  "=",
  ">",
  "?",
  "@",
  "[",
  "\\",
  "]",
  "^",
  "_",
  "`",
  "{",
  "|",
  "}",
  "~",
];

export const PASSWORD_POLICY: PasswordPolicy = {
  minLength: 8,
  requireUppercase: true,
  requireLowercase: true,
  requireDigits: true,
  requireSymbols: true,
};

function hasUppercase(pw: string): boolean {
  return /[A-Z]/.test(pw);
}
function hasLowercase(pw: string): boolean {
  return /[a-z]/.test(pw);
}
function hasDigit(pw: string): boolean {
  return /[0-9]/.test(pw);
}
function hasAllowedSymbol(pw: string): boolean {
  // 許容記号セットのいずれかを含むか（非ASCII記号は不可）
  const set = new Set(ALLOWED_SYMBOLS);
  for (const ch of pw) {
    if (set.has(ch)) return true;
  }
  return false;
}

export function meetsPolicy(pw: string): boolean {
  const lengthOk = pw.length >= PASSWORD_POLICY.minLength;
  const upperOk = !PASSWORD_POLICY.requireUppercase || hasUppercase(pw);
  const lowerOk = !PASSWORD_POLICY.requireLowercase || hasLowercase(pw);
  const digitOk = !PASSWORD_POLICY.requireDigits || hasDigit(pw);
  const symbolOk = !PASSWORD_POLICY.requireSymbols || hasAllowedSymbol(pw);
  return lengthOk && upperOk && lowerOk && digitOk && symbolOk;
}

export function getPolicyHelpText(): string {
  const symbols = ALLOWED_SYMBOLS.join(" ");
  return (
    `\n` +
    `8文字以上、英大文字・英小文字・数字・記号を各1個以上含めてください。\n` +
    `使用できる記号: ${symbols}`
  );
}

/**
 * 新しいパスワードの規則の文言。backend2 の PATIENT_AUTH_PASSWORD_POLICY_VIOLATION の文言と表現
 * （「半角の」「それぞれ1文字以上」）をそろえる。患者の画面の案内はこの定数から作る。
 */
export const PASSWORD_RULE_TEXT =
  "半角の英大文字・英小文字・数字・記号をそれぞれ1文字以上含む8文字以上で設定してください。";

/**
 * 患者の新しいパスワードの案内（Issue #473）。サーバーが拒否する推測できる値も案内する。
 *
 * @param kind initial は仮パスワードでログインした後の設定、change はログインした後の変更
 */
export function getPatientPolicyHelpText(kind: "initial" | "change"): string {
  const guessable =
    kind === "initial"
      ? "生年月日を含む値と、仮パスワードは使えません。"
      : "生年月日を含む値、仮パスワード、今のパスワードと同じ値は使えません。";
  return [
    PASSWORD_RULE_TEXT,
    `使用できる記号: ${ALLOWED_SYMBOLS.join(" ")}`,
    guessable,
  ].join("\n");
}
